'use client';

/**
 * Fires select_item event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { fireEvent } = useSelectItemEvent();
 * fireEvent(items);
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { EVENT_SELECT_ITEM } from 'constants/analyticsConstants';
import { OrderItemPrice } from 'services/cart/types';
import { OrderItemPart } from 'services/cart';
import { getAttributeValueForCart } from 'utils/gaEventAnalytics';
import {
  SearchItemSummaryData,
  SearchSelectItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';

type AddToCartType = {
  item: OrderItemPart | SearchSelectItemData;
  priceData?: OrderItemPrice;
  partName: string;
  locationId: string;
  itemSummary: Product | SearchItemSummaryData | Product[];
  isSearchPageEvent?: boolean;
  itemListName?: string;
  itemListId?: string;
  index?: number;
  serialNumber?: string;
  fitment?: string;
  relatedModel?: string;
};

type BuildItemDataContext = {
  item: OrderItemPart | SearchSelectItemData;
  element;
  priceData?: OrderItemPrice;
  partName: string;
  locationId: string;
  isSearchPageEvent: boolean;
  index: number;
};

type BuildItemDataMeta = {
  availabilityMessage: string;
  productPrice: number;
  weight?: number | string;
};

type BuildItemDataOptions = {
  serialNumber?: string;
  fitment?: string;
  relatedModel?: string;
};

const extractCategories = (categoryPath?: string) => {
  return categoryPath?.split('/') ?? [];
};

const buildItemData = (
  context: BuildItemDataContext,
  meta: BuildItemDataMeta,
  options: BuildItemDataOptions
) => {
  const {
    item,
    element,
    priceData,
    partName,
    locationId,
    isSearchPageEvent,
    index
  } = context;

  const { availabilityMessage, productPrice, weight } = meta;
  const { serialNumber, fitment, relatedModel } = options;
  const quantity = element?.quantity ?? item?.quantity;

  const [
    item_category,
    item_category2,
    item_category3,
    item_category4,
    item_category5
  ] = extractCategories(element?.categoryPath);

  return {
    item_id: item?.partNumber || element?.partNumber,
    item_name:
      element?.partNumber && element?.name
        ? `${element.partNumber}: ${element.name}`
        : partName,
    discount: !isSearchPageEvent
      ? priceData?.priceWithGST?.unformatted?.discountAmount
      : (item as SearchSelectItemData).discountAmount || 0,
    index,
    item_brand: element?.manufacturer,
    item_category,
    item_category2,
    item_category3,
    item_category4,
    item_category5,
    location_id: locationId,
    price: productPrice || element?.unformattedTotalPrice,
    quantity: quantity ?? 1,
    p_inventory_status: availabilityMessage,
    p_weight: weight || element?.weight,
    p_serial_number: serialNumber,
    ...(!!relatedModel && { p_related_model: relatedModel }),
    p_check_fitment: serialNumber ? fitment : undefined
  };
};

export const useSelectItemEvent = () => {
  const buildEvent = useCallback((params: AddToCartType) => {
    const {
      item,
      priceData,
      partName,
      locationId,
      itemSummary,
      isSearchPageEvent = false,
      itemListId,
      itemListName,
      index = 1,
      serialNumber,
      fitment,
      relatedModel
    } = params;

    const cartDetails = {
      cartId: (item as OrderItemPart).orderItemId
    };

    const { availabilityMessage } = !isSearchPageEvent
      ? getAttributeValueForCart(
          [priceData],
          (item as OrderItemPart).orderItemId
        )
      : (item as SearchSelectItemData);

    const { totalPrice = 0, weight } = item as SearchSelectItemData;
    const productPrice = !isSearchPageEvent
      ? priceData?.priceWithGST.unformatted.totalPrice
      : totalPrice;

    const itemData = Array.isArray(itemSummary) ? itemSummary : [itemSummary];
    const addItems = itemData.map((element) =>
      buildItemData(
        {
          item,
          element,
          priceData,
          partName,
          locationId,
          isSearchPageEvent,
          index
        },
        {
          availabilityMessage,
          productPrice,
          weight
        },
        {
          serialNumber,
          fitment,
          relatedModel
        }
      )
    );

    return {
      cartDetails,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: addItems
      },
      event: EVENT_SELECT_ITEM
    };
  }, []);

  const fireEvent = useCallback(
    (data: AddToCartType) => {
      sendGTMEvent({ ecommerce: null });
      sendGTMEvent(buildEvent(data));
    },
    [buildEvent]
  );

  return { fireEvent };
};
